import { DocumentType } from "encamp-prisma/generated/browser";
import invariant from "tiny-invariant";

import { TierIIRecordKind } from "../generated-graphql";
import { prettyPrintEnumValue } from "../utils/prettyPrintEnumValue";
import { getTenantNamingOverride } from "./tenantNaming";

export { deriveDocumentName } from "./deriveDocumentName";
export {
  getNamingFormatHelpText,
  getTenantNamingOverride,
  TENANT_DOCUMENT_NAMING_OVERRIDES,
} from "./tenantNaming";
export type { DocumentNamingContext, DocumentNamingOverride } from "./tenantNaming";

export function mapTierIIRecordKindToDocumentType(kind: TierIIRecordKind): DocumentType {
  const kindToDocumentType = {
    AST_DOCUMENTATION: DocumentType.ASTDocumentation,
    DIKE_DESCRIPTION: DocumentType.DikeDescription,
    EMERGENCY_RESPONSE_PLAN: DocumentType.EmergencyResponsePlan,
    HAZARDOUS_MATERIALS_INVENTORY_STATEMENT: DocumentType.HazardousMaterialsInventoryStatement,
    HAZARDOUS_MATERIALS_MANAGEMENT_PLAN: DocumentType.HazardousMaterialsManagementPlan,
    HAZARDOUS_MATERIALS_CONTINGENCY_PLAN_OR_QUICK_GUIDE: DocumentType.Other,
    INVOICE: DocumentType.Invoice,
    OTHER: DocumentType.Other,
    RECEIPT: DocumentType.Receipt,
    REFERENCE_IMAGE: DocumentType.ReferenceImage,
    REPORT: DocumentType.Report,
    SAFEGUARD_MEASURES: DocumentType.SafeguardMeasures,
    SCREENSHOT: DocumentType.Screenshot,
    SDS: DocumentType.SafetyDataSheet,
    SITE_COORDINATE_ABBREVIATION: DocumentType.SiteCoordinateAbbreviation,
    SITE_PLAN: DocumentType.SitePlan,
    TRAINING_PLAN: DocumentType.TrainingPlan,
    UST_DOCUMENTATION: DocumentType.USTDocumentation,
  };

  return kindToDocumentType[kind] ?? DocumentType.Other;
}

/**
 * Document types that are set exclusively by backend/system ingest and must
 * never be user-assignable. Excluded from document-type pickers and rejected by
 * the document upsert resolver. (RCRAManifestPrintedCopy is the EPA printed-copy
 * scan warehoused by the RCRAInfo poll.)
 */
export const SYSTEM_MANAGED_DOCUMENT_TYPES: DocumentType[] = [DocumentType.RCRAManifestPrintedCopy];

const systemManagedDocumentTypeValues: ReadonlySet<string> = new Set(
  SYSTEM_MANAGED_DOCUMENT_TYPES.map((t) => t.toString())
);

/**
 * String-based check so it works against either the Prisma or GraphQL
 * DocumentType enum (their values are identical strings).
 */
export function isSystemManagedDocumentType(documentType: string | null | undefined): boolean {
  if (!documentType) return false;
  return systemManagedDocumentTypeValues.has(documentType);
}

/**
 * Document types the nightly AI-enrichment cron must never auto-extract.
 *
 * Deliberately distinct from SYSTEM_MANAGED_DOCUMENT_TYPES: that list governs
 * whether a type is user-assignable (pickers + upsert guard), this one governs
 * whether the cron auto-parses it. A type could be system-managed yet still
 * auto-parseable, or auto-extraction-excluded yet user-assignable — they answer
 * different questions and are kept separate even though they happen to overlap.
 * An explicit documentId-scoped parse request bypasses this exclusion — see
 * buildDocumentWhereClause.
 *
 * Manifest documents are parsed on demand, never by the nightly sweep:
 *   - the manifest upload flow dispatches an explicit extraction immediately, and
 *   - staff can parse any document on demand from the Manifest Documents grid.
 * Excluding them lets a manifest be attached *without* parsing (it simply stays
 * unparsed until someone parses it) rather than being silently auto-parsed
 * overnight. RCRAManifestPrintedCopy is also system-managed; RCRAManifest is not.
 */
export const AUTO_EXTRACTION_EXCLUDED_DOCUMENT_TYPES: DocumentType[] = [
  DocumentType.RCRAManifest,
  DocumentType.RCRAManifestPrintedCopy,
];

const autoExtractionExcludedDocumentTypeValues: ReadonlySet<string> = new Set(
  AUTO_EXTRACTION_EXCLUDED_DOCUMENT_TYPES.map((t) => t.toString())
);

/**
 * String-based check so it works against either the Prisma or GraphQL
 * DocumentType enum (their values are identical strings).
 */
export function isAutoExtractionExcludedDocumentType(
  documentType: string | null | undefined
): boolean {
  if (!documentType) return false;
  return autoExtractionExcludedDocumentTypeValues.has(documentType);
}

/**
 * This is a list of DocumentTypes that are considered for submitting a Tier II report
 * regardless of jurisdiction; a list of types of documents that _might_ be
 * needed to fulfill a report.
 */
export const KnownSubmissionDocumentTypes: DocumentType[] = [
  DocumentType.ASTDocumentation,
  DocumentType.DikeDescription,
  DocumentType.EmergencyResponsePlan,
  DocumentType.HazardousMaterialsContingencyPlanOrQuickGuide,
  DocumentType.HazardousWasteContingencyPlan,
  DocumentType.SafeguardMeasures,
  DocumentType.SiteCoordinateAbbreviation,
  DocumentType.SitePlan,
  DocumentType.TrainingPlan,
  DocumentType.USTDocumentation,
  // CERS HMBP document types
  DocumentType.USTMonitoringSitePlan,
  DocumentType.USTResponsePlan,
  DocumentType.USTOwnerOperatorWrittenAgreement,
  DocumentType.USTLetterFromChiefFinancialOfficer,
  DocumentType.OwnerStatementOfDesignatedUSTOperatorCompliance,
  DocumentType.USTCertificationOfFinancialResponsibility,
  DocumentType.RecyclableMaterialsReportDocumentation,
  DocumentType.HazardousWasteTankClosureCertificate,
  DocumentType.AboveGroundPetroleumStorageActDocumentation,
  DocumentType.AboveGroundSeparationContainmentAndMonitoringPlan,
  DocumentType.SpillPreventionControlAndCountermeasurePlan,
];

/**
 * Removes duplicate extensions from a filename.
 *
 * For example, if the filename is "report.pdf.pdf", this function will return "report.pdf".
 */
export function removeDuplicateExtension(filename: string) {
  // Split the filename by dots
  const parts = filename.split(".");

  // If we have 3 or more parts and the last two are identical
  if (parts.length >= 3 && parts[parts.length - 1] === parts[parts.length - 2]) {
    // Remove the last extension and join back together
    parts.pop();
    return parts.join(".");
  }

  // Return original filename if no duplicate extension found
  return filename;
}

export function convertBytesToMegabytes(bytes: number | null): number | null {
  if (bytes === null) return null;
  if (bytes === 0) return 0;

  const megabytes = bytes / (1024 * 1024);

  // If the value is nonzero but rounds to 0, show a minimum value
  if (megabytes > 0 && megabytes < 0.000001) {
    return 0.000001;
  }

  return Number(megabytes.toFixed(6));
}

/**
 * Replaces all characters that aren't letters, numbers, or dashes with underscores.
 * This ensures the string is safe to use as a filename.
 * @example "Hello, World!" -> "Hello_World_"
 */
export function cleanStringForFilesystem(str: string): string {
  // [^a-zA-Z0-9-] means "match any character that is NOT a-z, A-Z, 0-9, or -"
  // and then replace any multiple underscores with a single underscore.
  return str.replace(/[^a-zA-Z0-9-]/g, "_").replace(/_+/g, "_");
}

/**
 * Formats a document type enum value into a human-readable string.
 * This was basically copied from oak/src/util/constants.ts except uses Prisma types rather than graphql types.
 */
export function prettyPrintDocumentType(documentType: DocumentType): string {
  switch (documentType) {
    case DocumentType.ASTDocumentation:
      return "AST Documentation";
    case DocumentType.Certification:
      return "Certification";
    case DocumentType.Check:
      return "Check";
    case DocumentType.CoverLetter:
      return "Cover Letter";
    case DocumentType.DikeDescription:
      return "Dike Description";
    case DocumentType.EmergencyResponsePlan:
      return "Emergency Response Plan";
    case DocumentType.EPCRA302:
      return "EPCRA 302";
    case DocumentType.EPCRA304:
      return "EPCRA 304";
    case DocumentType.EPCRA311:
      return "EPCRA 311";
    case DocumentType.EPCRA312:
      return "EPCRA 312";
    case DocumentType.EPCRA313:
      return "EPCRA 313";
    case DocumentType.FeeCalculation:
      return "Fee Calculation";
    case DocumentType.Fees:
      return "Fees";
    case DocumentType.Form:
      return "Form";
    case DocumentType.HazardousMaterialsContingencyPlanOrQuickGuide:
      return "Hazardous Materials Contingency Plan or Quick Guide";
    case DocumentType.HazardousMaterialsInventoryStatement:
      return "Hazardous Materials Inventory Statement";
    case DocumentType.HazardousMaterialsManagementPlan:
      return "Hazardous Materials Management Plan";
    case DocumentType.HazardousWasteAnnualReport:
      return "Hazardous Waste Annual Report";
    case DocumentType.HazardousWasteBiennialReport:
      return "Hazardous Waste Biennial Report";
    case DocumentType.HazardousWasteContingencyPlan:
      return "Hazardous Waste Contingency Plan";
    case DocumentType.HazardousWasteFeesReceipts:
      return "Hazardous Waste Fees/Receipts";
    case DocumentType.HazardousWasteNotification:
      return "Hazardous Waste Notification";
    case DocumentType.HazardousWasteQuarterlyReport:
      return "Hazardous Waste Quarterly Report";
    case DocumentType.HazardousWasteRecertification:
      return "Hazardous Waste Recertification";
    case DocumentType.Invoice:
      return "Invoice";
    case DocumentType.NegativeDeclaration:
      return "Negative Declaration";
    case DocumentType.Other:
      return "Other";
    case DocumentType.OtherRCRA:
      return "Other RCRA report";
    case DocumentType.P2Plan:
      return "P2 Plan";
    case DocumentType.P2Update:
      return "P2 Update";
    case DocumentType.Permit:
      return "Permit";
    case DocumentType.Proof:
      return "Proof";
    case DocumentType.RCRAAnnual:
      return "RCRA Annual";
    case DocumentType.RCRAFeePaymentOrInvoice:
      return "RCRA Fee Payment/Invoice";
    case DocumentType.RCRAManifest:
      return "RCRA Manifest";
    case DocumentType.RCRAManifestPrintedCopy:
      return "RCRAInfo Manifest Printed Copy";
    case DocumentType.RCRAUpdateNotification:
      return "RCRA Update Notification";
    case DocumentType.Receipt:
      return "Receipt";
    case DocumentType.ReferenceImage:
      return "Reference Image";
    case DocumentType.Report:
      return "Report";
    case DocumentType.RevisionOrUpdate:
      return "Revision/Update";
    case DocumentType.SafeguardMeasures:
      return "Safeguard Measures";
    case DocumentType.SafetyDataSheet:
      return "Safety Data Sheet";
    case DocumentType.Screenshot:
      return "Screenshot";
    case DocumentType.SiteCoordinateAbbreviation:
      return "Site Coordinate Abbreviation";
    case DocumentType.SitePlan:
      return "Site Plan";
    case DocumentType.Submission:
      return "Submission";
    case DocumentType.Tier2Submit:
      return "Tier II Submit";
    case DocumentType.TrainingPlan:
      return "Training Plan";
    case DocumentType.USTDocumentation:
      return "UST Documentation";
    case DocumentType.USTMonitoringSitePlan:
      return "UST Monitoring Site Plan";
    case DocumentType.USTCertificationOfFinancialResponsibility:
      return "UST Certification of Financial Responsibility";
    case DocumentType.USTResponsePlan:
      return "UST Response Plan";
    case DocumentType.USTOwnerOperatorWrittenAgreement:
      return "UST Owner/Operator: Written Agreement";
    case DocumentType.USTLetterFromChiefFinancialOfficer:
      return "UST Letter from Chief Financial Officer";
    case DocumentType.OwnerStatementOfDesignatedUSTOperatorCompliance:
      return "Owner Statement of Designated UST Operator Compliance";
    case DocumentType.RecyclableMaterialsReportDocumentation:
      return "Recyclable Materials Report Documentation";
    case DocumentType.HazardousWasteTankClosureCertificate:
      return "Hazardous Waste Tank Closure Certificate";
    case DocumentType.AboveGroundPetroleumStorageActDocumentation:
      return "Above Ground Petroleum Storage Act Documentation";
    case DocumentType.AboveGroundSeparationContainmentAndMonitoringPlan:
      return "Above Ground Separation, Containment, and Monitoring Plan";
    case DocumentType.SpillPreventionControlAndCountermeasurePlan:
      return "Spill Prevention, Control, and Countermeasure Plan";
    case null:
    case undefined:
    case "" as DocumentType: // just to protect against runtime malfeasance
      return "";
    default:
      return prettyPrintEnumValue(documentType);
  }
}

/**
 * Formats a document name based on its type, facility name, date, and other parameters.
 *
 * Examples:
 * A certification for my facility on 2024-01-01 would be:
 * - "Certification_My_Facility_2024-01-01.pdf"
 * A site plan for my activity on the report for my facility on 2024-01-01 would be:
 * - "Site_Plan_My_Facility_2024-01-01.pdf"
 * A Report for my facility on 2024-01-01 would be:
 * - "Report_My_Facility_2024-01-01.pdf"
 * A Receipt for multiple facilities on 2024-01-01 would be:
 * - "Receipt_2024-01-01.pdf"
 *
 * If `tenantId` has a registered entry in TENANT_DOCUMENT_NAMING_OVERRIDES,
 * the override fully owns the title format and `titlePrefix` is ignored.
 */
export function formatDocumentName({
  document,
  facilityName,
  date,
  titlePrefix,
  tenantId,
  reportingYear,
  documentName,
}: {
  document: Pick<
    { documentType: DocumentType; fileExtension: string },
    "documentType" | "fileExtension"
  >;
  facilityName: string;
  date?: Date;
  titlePrefix?: string;
  tenantId?: string | null;
  reportingYear?: number | null;
  documentName?: string;
}): string {
  invariant(document.documentType, "Document type is required to generate a document name");
  const dateToUse = date ?? new Date();
  const override = getTenantNamingOverride(tenantId);
  if (override) {
    return override({
      documentType: document.documentType,
      facilityName: facilityName ?? "",
      reportingYear: reportingYear ?? null,
      date: dateToUse,
      fileExtension: document.fileExtension,
      documentName,
    });
  }
  const formattedDocumentType = prettyPrintDocumentType(document.documentType);
  const formattedDate = dateToUse.toISOString().split("T")[0];
  const combined = [titlePrefix, formattedDocumentType, facilityName, formattedDate]
    .filter((x) => !!x && x.length > 0)
    .join("_");
  return `${cleanStringForFilesystem(combined)}.${document.fileExtension}`;
}
